import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Group,
  Loader,
  Paper,
  Select,
  Stack,
  Table,
  Text,
  TextInput,
  Title,
} from '@mantine/core'
import { useForm } from '@mantine/form'
import { modals } from '@mantine/modals'
import { showNotification } from '@mantine/notifications'
import { normalizeErrorString } from '@medplum/core'
import { useEffect, useState } from 'react'
import {
  IconCircleOff,
  IconInfoCircle,
  IconPencil,
  IconPlus,
  IconTrash,
  IconX,
} from '@tabler/icons-react'
import { CustomInsurancePayerModal } from '@/components/ui/CustomInsurancePayerModal'
import { Navigation } from './components'
import { useMiddlewareClient } from '@/hooks/useMiddlewareClient'
import {
  deleteInsurancePayer,
  deleteInsurancePlan,
  getInsurancePayerDetails,
  getInsurancePayersList,
  upsertInsurancePlan,
  type InsurancePayerDetails,
  type InsurancePayerOption,
  type InsurancePlanListItem,
} from './insurancePlans.service'
import {
  INSURANCE_LOB_CATEGORY_OPTIONS,
  INSURANCE_PRODUCT_TYPE_OPTIONS,
} from './insurancePlans.constants'

type InsurancePlanFormValues = {
  planParamId?: string
  planName: string
  productType: string
  planId: string
  lobCategory: string
}

const EMPTY_FORM_VALUES: InsurancePlanFormValues = {
  planParamId: undefined,
  planName: '',
  productType: '',
  planId: '',
  lobCategory: '',
}

export function InsurancePlans() {
  const middlewareClient = useMiddlewareClient()
  const [payerOptions, setPayerOptions] = useState<InsurancePayerOption[]>([])
  const [selectedPayer, setSelectedPayer] = useState<string | null>(null)
  const [payerDetails, setPayerDetails] = useState<InsurancePayerDetails | null>(null)
  const [editingPayer, setEditingPayer] = useState<InsurancePayerOption | null>(null)
  const [isPayersLoading, setIsPayersLoading] = useState(true)
  const [isDetailsLoading, setIsDetailsLoading] = useState(false)
  const [isSaving, setIsSaving] = useState(false)
  const [deletingPayerParamId, setDeletingPayerParamId] = useState<string | null>(null)
  const [deletingParamId, setDeletingParamId] = useState<string | null>(null)
  const [isPayerModalOpen, setIsPayerModalOpen] = useState(false)

  const form = useForm<InsurancePlanFormValues>({
    mode: 'uncontrolled',
    initialValues: EMPTY_FORM_VALUES,
    validate: {
      planName: (value) => (!value.trim() ? 'Plan name is required' : null),
      productType: (value) => (!value.trim() ? 'Product type is required' : null),
      planId: (value) => (!value.trim() ? 'Plan ID is required' : null),
      lobCategory: (value) => (!value.trim() ? 'LOB category is required' : null),
    },
  })

  const loadPayers = async (preferredPayerId?: string | null) => {
    setIsPayersLoading(true)
    try {
      const result = await getInsurancePayersList(middlewareClient)
      setPayerOptions(result)
      setSelectedPayer((currentValue) => {
        if (preferredPayerId && result.some((item) => item.id === preferredPayerId)) {
          return preferredPayerId
        }
        if (currentValue && result.some((item) => item.id === currentValue)) {
          return currentValue
        }
        return result[0]?.id ?? null
      })
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(err) || 'Failed to load insurance payers.',
      })
    } finally {
      setIsPayersLoading(false)
    }
  }

  useEffect(() => {
    void loadPayers()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [middlewareClient])

  useEffect(() => {
    let isActive = true

    const loadDetails = async () => {
      if (!selectedPayer) {
        setPayerDetails(null)
        return
      }

      setIsDetailsLoading(true)
      try {
        const result = await getInsurancePayerDetails(middlewareClient, selectedPayer)
        if (!isActive) {
          return
        }
        setPayerDetails(result)
        form.setValues(EMPTY_FORM_VALUES)
      } catch (err) {
        if (!isActive) {
          return
        }
        showNotification({
          color: 'red',
          icon: <IconCircleOff />,
          title: 'Error',
          message: normalizeErrorString(err) || 'Failed to load insurance plans.',
        })
        setPayerDetails(null)
      } finally {
        if (isActive) {
          setIsDetailsLoading(false)
        }
      }
    }

    void loadDetails()

    return () => {
      isActive = false
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [middlewareClient, selectedPayer])

  const reloadSelectedPayer = async () => {
    if (!selectedPayer) {
      return
    }

    const result = await getInsurancePayerDetails(middlewareClient, selectedPayer)
    setPayerDetails(result)
  }

  const selectedPayerOption = payerOptions.find((item) => item.id === selectedPayer) ?? null

  const beginEdit = (plan: InsurancePlanListItem) => {
    if (!plan.editable) {
      return
    }

    form.setValues({
      planParamId: plan.parameterId,
      planName: plan.name,
      productType: plan.productType,
      planId: plan.id,
      lobCategory: plan.lobCategory,
    })
  }

  const resetForm = () => {
    form.setValues(EMPTY_FORM_VALUES)
  }

  const handleSubmit = async (values: InsurancePlanFormValues) => {
    if (!payerDetails || isSaving) {
      return
    }

    setIsSaving(true)
    try {
      await upsertInsurancePlan(middlewareClient, {
        payerBlockId: payerDetails.id,
        planParamId: values.planParamId,
        planName: values.planName.trim(),
        productType: values.productType,
        planId: values.planId.trim(),
        lobCategory: values.lobCategory,
      })
      await reloadSelectedPayer()
      resetForm()

      showNotification({
        color: 'green',
        title: values.planParamId ? 'Plan updated' : 'Plan created',
        message: values.planParamId
          ? 'Custom insurance plan updated successfully.'
          : 'Custom insurance plan created successfully.',
      })
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(err) || 'Failed to save insurance plan.',
      })
    } finally {
      setIsSaving(false)
    }
  }

  const handleDelete = async (planParamId: string) => {
    setDeletingParamId(planParamId)
    try {
      await deleteInsurancePlan(middlewareClient, planParamId)
      await reloadSelectedPayer()
      if (form.getValues().planParamId === planParamId) {
        resetForm()
      }
      showNotification({
        color: 'green',
        title: 'Plan deleted',
        message: 'Custom insurance plan deleted successfully.',
      })
    } catch (err) {
      showNotification({
        color: 'red',
        icon: <IconCircleOff />,
        title: 'Error',
        message: normalizeErrorString(err) || 'Failed to delete insurance plan.',
      })
    } finally {
      setDeletingParamId(null)
    }
  }

  const handleDeletePayer = async () => {
    if (!selectedPayerOption?.parameterId) {
      return
    }

    modals.openConfirmModal({
      title: 'Delete Custom Payer',
      children:
        'Delete this custom payer section? This only works when no custom plans remain under it.',
      labels: { confirm: 'Delete', cancel: 'Cancel' },
      confirmProps: { color: 'red' },
      onConfirm: async () => {
        setDeletingPayerParamId(selectedPayerOption.parameterId ?? null)
        try {
          await deleteInsurancePayer(middlewareClient, selectedPayerOption.parameterId!)
          resetForm()
          setPayerDetails(null)
          await loadPayers(null)
          showNotification({
            color: 'green',
            title: 'Payer deleted',
            message: 'Custom insurance payer deleted successfully.',
          })
        } catch (err) {
          showNotification({
            color: 'red',
            icon: <IconCircleOff />,
            title: 'Error',
            message: normalizeErrorString(err) || 'Failed to delete insurance payer.',
          })
        } finally {
          setDeletingPayerParamId(null)
        }
      },
    })
  }

  return (
    <>
      <Navigation />
      <Stack p="lg">
        <div>
          <Title order={2}>Insurance Plans</Title>
          <Text c="dimmed">
            Shared payer catalogs stay read-only. Add custom payer sections and custom plans for
            your organization on top.
          </Text>
        </div>

        <Paper withBorder p="lg">
          <Stack>
            <Group justify="space-between" align="flex-end">
              <Select
                className="flex-1"
                label="Payer / State"
                placeholder={isPayersLoading ? 'Loading payers...' : 'Select a payer'}
                value={selectedPayer}
                onChange={setSelectedPayer}
                data={payerOptions.map((item) => ({
                  value: item.id,
                  label: item.state ? `${item.payerName} ${item.state}` : item.payerName,
                }))}
                searchable
                disabled={isPayersLoading}
              />
              <Group>
                <Button
                  leftSection={<IconPlus size={16} />}
                  onClick={() => {
                    setEditingPayer(null)
                    setIsPayerModalOpen(true)
                  }}
                >
                  Create payer
                </Button>
                <Button
                  variant="subtle"
                  color="gray"
                  leftSection={<IconPencil size={16} />}
                  disabled={!selectedPayerOption?.editable}
                  onClick={() => {
                    if (!selectedPayerOption?.editable) {
                      return
                    }
                    setEditingPayer(selectedPayerOption)
                    setIsPayerModalOpen(true)
                  }}
                >
                  Edit payer
                </Button>
                <Button
                  variant="subtle"
                  color="red"
                  leftSection={<IconTrash size={16} />}
                  disabled={!selectedPayerOption?.deletable || deletingPayerParamId !== null}
                  loading={deletingPayerParamId === selectedPayerOption?.parameterId}
                  onClick={() => void handleDeletePayer()}
                >
                  Delete payer
                </Button>
              </Group>
            </Group>

            <Alert icon={<IconInfoCircle size={16} />} color="blue" variant="light">
              Shared payers and plans are visible here for reference and cannot be edited. Custom
              payer sections and plans are stored per organization.
            </Alert>
          </Stack>
        </Paper>

        <Paper
          withBorder
          p="lg"
          className={`max-w-200 ${form.getValues().planParamId ? 'bg-[#fff3d2]!' : ''}`}
        >
          <Stack>
            <Group justify="space-between" align="center">
              <div>
                <Title order={4}>
                  {form.getValues().planParamId ? 'Edit Custom Plan' : 'Add Custom Plan'}
                </Title>
                <Text c="dimmed">
                  Custom plans are scoped to the currently selected payer/state entry and your
                  organization.
                </Text>
              </div>
              {form.getValues().planParamId && (
                <Button
                  variant="subtle"
                  color="gray"
                  leftSection={<IconX size={16} />}
                  onClick={resetForm}
                >
                  Cancel edit
                </Button>
              )}
            </Group>

            <form onSubmit={form.onSubmit(handleSubmit)}>
              <Stack>
                <TextInput
                  label="Plan Name"
                  placeholder="Enter plan name"
                  key={form.key('planName')}
                  {...form.getInputProps('planName')}
                  disabled={!payerDetails || isSaving}
                />
                <Select
                  label="Product Type"
                  placeholder="Select a product type"
                  data={INSURANCE_PRODUCT_TYPE_OPTIONS}
                  key={form.key('productType')}
                  {...form.getInputProps('productType')}
                  disabled={!payerDetails || isSaving}
                />
                <TextInput
                  label="Plan ID"
                  placeholder="Enter plan ID"
                  key={form.key('planId')}
                  {...form.getInputProps('planId')}
                  disabled={!payerDetails || isSaving}
                />
                <Select
                  label="LOB Category"
                  placeholder="Select a LOB category"
                  data={INSURANCE_LOB_CATEGORY_OPTIONS}
                  key={form.key('lobCategory')}
                  {...form.getInputProps('lobCategory')}
                  disabled={!payerDetails || isSaving}
                />
                <Group justify="flex-end">
                  <Button type="submit" loading={isSaving} disabled={!payerDetails}>
                    {form.getValues().planParamId ? 'Update plan' : 'Create plan'}
                  </Button>
                </Group>
              </Stack>
            </form>
          </Stack>
        </Paper>

        <Paper withBorder p="lg">
          <Stack>
            <Group justify="space-between" align="center">
              <div>
                <Title order={4}>Merged Plan Catalog</Title>
                {payerDetails && (
                  <Group gap="xs">
                    <Text c="dimmed">
                      {payerDetails.payerName} ({payerDetails.payerId}) • {payerDetails.state}
                    </Text>
                    <Badge
                      color={payerDetails.source === 'default' ? 'gray' : 'blue'}
                      variant="light"
                    >
                      {payerDetails.source}
                    </Badge>
                  </Group>
                )}
              </div>
              {isDetailsLoading && <Loader size="sm" />}
            </Group>

            {!isDetailsLoading && payerDetails && (
              <Table striped highlightOnHover>
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>Plan</Table.Th>
                    <Table.Th>Plan ID</Table.Th>
                    <Table.Th>Product Type</Table.Th>
                    <Table.Th>LOB</Table.Th>
                    <Table.Th>Source</Table.Th>
                    <Table.Th>Actions</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {payerDetails.plans.map((plan) => (
                    <Table.Tr key={`${plan.source}-${plan.parameterId ?? plan.id}`}>
                      <Table.Td>{plan.name}</Table.Td>
                      <Table.Td>{plan.id}</Table.Td>
                      <Table.Td>{plan.productType}</Table.Td>
                      <Table.Td>{plan.lobCategory}</Table.Td>
                      <Table.Td>
                        <Badge color={plan.source === 'default' ? 'gray' : 'blue'} variant="light">
                          {plan.source}
                        </Badge>
                      </Table.Td>
                      <Table.Td>
                        <Group gap="xs">
                          <ActionIcon
                            variant="subtle"
                            color="blue"
                            disabled={!plan.editable}
                            onClick={() => beginEdit(plan)}
                          >
                            <IconPencil size={16} />
                          </ActionIcon>
                          <ActionIcon
                            variant="subtle"
                            color="red"
                            disabled={!plan.deletable || deletingParamId === plan.parameterId}
                            loading={deletingParamId === plan.parameterId}
                            onClick={() => plan.parameterId && handleDelete(plan.parameterId)}
                          >
                            <IconTrash size={16} />
                          </ActionIcon>
                        </Group>
                      </Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            )}

            {!isDetailsLoading && !payerDetails && (
              <Text c="dimmed">Select a payer/state entry to manage plans.</Text>
            )}
          </Stack>
        </Paper>
      </Stack>

      <CustomInsurancePayerModal
        opened={isPayerModalOpen}
        initialValue={editingPayer}
        onClose={() => {
          setIsPayerModalOpen(false)
          setEditingPayer(null)
        }}
        onSuccess={(payer) => {
          void loadPayers(payer.id)
          setSelectedPayer(payer.id)
        }}
      />
    </>
  )
}
